import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowUpRight, Check, ChevronDown, LayoutDashboard, Settings as SettingsIcon, LogOut } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const login = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href =
    "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
};

const ROWS = [
  {
    tag: "Pipeline",
    title: "Track every application in one place",
    desc: "Save listings and move them through five clear stages — Saved, Applied, Interviewing, Offer, Rejected. Filter by status, priority, salary or location, and sort the list any way you like.",
    points: ["Five-stage pipeline", "Priority + salary filters", "One-tap status updates"],
    img: "/shots/jobs.jpeg",
    slug: "pipeline",
  },
  {
    tag: "Dashboard",
    title: "A command center for your job hunt",
    desc: "Open the app and immediately see what needs attention. Today's Focus surfaces your highest-priority active applications first, so you always know where to spend your energy.",
    points: ["Today's Focus list", "Live pipeline counts", "Recent activity at a glance"],
    img: "/shots/dashboard.jpeg",
    slug: "dashboard",
  },
  {
    tag: "AI",
    title: "Tailor resumes & cover letters with AI",
    desc: "From any job, let Claude rewrite your master resume to match the role and draft a sharp cover letter in seconds. Your facts, titles and dates stay exactly as you wrote them.",
    points: ["Role-matched resume tailoring", "One-click cover letters", "Facts stay intact"],
    img: "/shots/jobdetail.jpeg",
    slug: "ai",
  },
  {
    tag: "Resume",
    title: "One master resume, always ready",
    desc: "Build a structured resume once — import it straight from your LinkedIn PDF, polish the wording with AI, and export a clean PDF whenever you need it.",
    points: ["LinkedIn PDF import", "AI polish", "Clean PDF export"],
    img: "/shots/resume.jpeg",
    slug: "resume",
  },
  {
    tag: "Insights",
    title: "See what's actually working",
    desc: "Metrics show how long applications sit in each stage and how your activity trends over time — so you can spot bottlenecks and keep momentum.",
    points: ["Time-in-stage breakdown", "Applications over time", "Spot the bottlenecks"],
    img: "/shots/metrics.jpeg",
    slug: "insights",
  },
];

const Row = ({ row, flip, index }) => (
  <div
    data-testid={`about-row-${row.slug}`}
    className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center py-14 sm:py-20 border-t border-zinc-200"
  >
    <div className={"order-1 " + (flip ? "lg:order-2" : "lg:order-1")}>
      <div className="flex items-center gap-3">
        <span className="font-mono-jb text-xs text-zinc-400">{String(index + 1).padStart(2, "0")}</span>
        <span className="label-tiny">{row.tag}</span>
      </div>
      <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-4 leading-[1.1]">{row.title}</h2>
      <p className="text-base text-zinc-600 mt-4 leading-relaxed max-w-lg">{row.desc}</p>
      <ul className="mt-6 space-y-2">
        {row.points.map((p) => (
          <li key={p} className="flex items-center gap-3 text-sm text-zinc-800">
            <span className="w-5 h-5 bg-zinc-900 text-white flex items-center justify-center shrink-0"><Check size={12} /></span>
            {p}
          </li>
        ))}
      </ul>
    </div>
    <div className={"order-2 " + (flip ? "lg:order-1" : "lg:order-2")}>
      <div className="border-2 border-zinc-900 bg-zinc-50 overflow-hidden shadow-[8px_8px_0_0_#18181b]">
        <img
          src={row.img}
          alt={row.title}
          loading="lazy"
          className="block w-full h-auto"
          data-testid={`about-img-${row.slug}`}
        />
      </div>
    </div>
  </div>
);

export default function About() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-white text-zinc-900">
      {/* nav */}
      <header className="border-b border-zinc-200 sticky top-0 bg-white/90 backdrop-blur z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="about-logo-home">
            <Logo size={28} />
            <span className="label-tiny ml-1 hidden sm:inline">JOB TRACKER</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/" className="label-tiny text-zinc-600 hover:text-zinc-900 px-2 hidden sm:inline" data-testid="about-nav-home">Home</Link>
            {loading ? (
              <div className="w-7 h-7" />
            ) : user ? (
              <DropdownMenu>
                <DropdownMenuTrigger data-testid="about-user-menu-trigger" className="flex items-center gap-2 px-2 py-1 border border-transparent hover:border-zinc-200 transition-colors">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-7 h-7 border border-zinc-200" />
                  ) : (
                    <div className="w-7 h-7 bg-zinc-200" />
                  )}
                  <span className="hidden sm:inline text-sm font-medium max-w-[140px] truncate">{user.name || "Account"}</span>
                  <ChevronDown size={14} className="text-zinc-500" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-none w-56 border-zinc-200">
                  <div className="px-3 py-2 text-xs text-zinc-500 truncate">{user.email}</div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild data-testid="about-menu-dashboard">
                    <Link to="/dashboard" className="cursor-pointer">
                      <LayoutDashboard size={14} className="mr-2" /> Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild data-testid="about-menu-settings">
                    <Link to="/settings" className="cursor-pointer">
                      <SettingsIcon size={14} className="mr-2" /> Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="about-menu-logout" className="cursor-pointer">
                    <LogOut size={14} className="mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button data-testid="about-nav-signin" onClick={login} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white h-9 px-4 text-sm">
                Sign in
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* hero */}
      <section className="relative">
        <div className="grid-bg absolute inset-0 opacity-60 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="max-w-3xl">
            <div className="label-tiny mb-6">What is ReInVocation?</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
              The job hunt,<br/>
              <span className="text-zinc-400">organized end to end.</span>
            </h1>
            <p className="text-base sm:text-lg text-zinc-600 mt-6 max-w-xl leading-relaxed">
              ReInVocation is a no-nonsense workspace for serious job seekers — track every
              application, tailor your resume with AI, generate cover letters, and see what's
              working. Built for engineers and anyone who wants their search under control.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button data-testid="about-hero-cta" onClick={login} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white h-12 px-6 text-sm font-semibold">
                Get started — it's free <ArrowUpRight size={16} className="ml-2" />
              </Button>
              <span className="label-tiny">Free · No card required</span>
            </div>
          </div>
        </div>
      </section>

      {/* staggered feature rows */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6">
        {ROWS.map((row, i) => (
          <Row key={row.slug} row={row} index={i} flip={i % 2 === 1} />
        ))}
      </section>

      {/* closing CTA */}
      <section className="border-t border-zinc-200 bg-zinc-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Ready to take control of your search?</h2>
          <p className="text-zinc-400 mt-4 max-w-xl mx-auto leading-relaxed">
            Sign in with Google and start tracking your first application in under a minute.
          </p>
          <div className="mt-8 flex justify-center">
            <Button data-testid="about-footer-cta" onClick={login} className="rounded-none bg-white text-zinc-900 hover:bg-zinc-200 h-12 px-7 text-sm font-semibold">
              Continue with Google <ArrowUpRight size={16} className="ml-2" />
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-800 bg-zinc-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-4">
          <span className="label-tiny text-zinc-400">© ReInVocation</span>
          <Link to="/" className="label-tiny text-zinc-400 hover:text-white transition-colors" data-testid="about-footer-home">Back to home →</Link>
          <span className="label-tiny text-zinc-400">v1.0 · 2026</span>
        </div>
      </footer>
    </div>
  );
}
